import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

const BookingPieChart = ({ data }) => {
  const { t } = useTranslation();

  // Calculate totals from the data
  const totals = data.reduce((acc, month) => ({
    totalBookings: acc.totalBookings + (month.totalBookings || 0),
    totalCancelled: acc.totalCancelled + (month.totalCancelled || 0)
  }), {
    totalBookings: 0,
    totalCancelled: 0
  });

  const activeBookings = totals.totalBookings - totals.totalCancelled;

  const activeBookingsKey = t('admin.reports.charts.active_bookings');
  const cancelledBookingsKey = t('admin.reports.charts.cancelled_bookings');

  const pieData = [
    {
      name: activeBookingsKey,
      value: activeBookings,
      percentage: totals.totalBookings > 0 ? ((activeBookings / totals.totalBookings) * 100).toFixed(1) : 0
    },
    {
      name: cancelledBookingsKey,
      value: totals.totalCancelled,
      percentage: totals.totalBookings > 0 ? ((totals.totalCancelled / totals.totalBookings) * 100).toFixed(1) : 0
    }
  ];

  const COLORS = {
    [activeBookingsKey]: '#10b981',
    [cancelledBookingsKey]: '#ef4444'
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{data.name}</p>
          <p className="tooltip-item" style={{ color: COLORS[data.name] }}>
            {t('admin.reports.charts.count')}: {data.value}
          </p>
          <p className="tooltip-item" style={{ color: COLORS[data.name] }}>
            {t('admin.reports.charts.percentage')}: {data.percentage}%
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, percentage }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (value === 0) return null;

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${percentage}%`}
      </text>
    );
  };

  return (
    <div className="booking-pie-chart-container">
      <div className="chart-header">
        <h2 className="chart-title">{t('admin.reports.charts.status_distribution_title')}</h2>
        <p className="chart-subtitle">{t('admin.reports.charts.status_distribution_subtitle')}</p>
      </div>
      
      <div className="chart-wrapper">
        {totals.totalBookings > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value, entry) => (
                  <span style={{ color: entry.color, fontWeight: '500' }}>
                    {value}: {entry.payload.value} ({entry.payload.percentage}%)
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-data-chart">
            <p>{t('admin.reports.no_data')}</p>
          </div>
        )}
      </div>
      
      <div className="pie-chart-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">{t('admin.reports.charts.total_bookings')}:</span>
            <span className="stat-value">{totals.totalBookings}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('admin.reports.charts.active_bookings')}:</span>
            <span className="stat-value" style={{ color: '#10b981' }}>{activeBookings}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('admin.reports.charts.cancelled_bookings')}:</span>
            <span className="stat-value" style={{ color: '#ef4444' }}>{totals.totalCancelled}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPieChart;